/**
 * Composant de démarrage de l'application
 * Initialisation de la navigation et du menu
 */
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { MenuController } from '@ionic/angular';
import { ObjectHelper } from 'apps/idl/src/helpers';
import { IAuthStatus } from 'apps/idl/src/model';
import { IAppStartPauseLogData } from 'apps/idl/src/model/application/iapp-start-pause-log-data';
import { ConfigData } from 'apps/idl/src/model/config/ConfigData';
import { IConfig } from 'apps/idl/src/model/config/IConfig';
import { ActivePageManager } from 'apps/idl/src/model/navigation/ActivePageManager';
import { DeviceNotAuthorizedError } from 'apps/idl/src/model/store/error/device-not-authorized-error';
import { BatteryService } from 'apps/idl/src/modules/battery/services/battery.service';
import { DeeplinkService } from 'apps/idl/src/modules/deeplink/deeplink.service';
import { LoggerService } from 'apps/idl/src/modules/logger/services/logger.service';
import { PwaUpdateService } from 'apps/idl/src/modules/pwa/services/pwa-update.service';
import { ApplicationService, EntityLinkService, FlagService, PlatformService, SecurityService } from 'apps/idl/src/services/security';
import { ConfigService } from 'apps/idl/src/services/security/config.service';
import { CapacitorPluginScePlugin } from 'capacitor-plugin-sce';
import { PanneauDesktopComponent, PopoverComponent, SwipeableDrawerComponent } from 'lighting-up-angular';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { mergeMap, tap } from 'rxjs/internal/operators';
import packageJson from '../../package.json';
import * as constants from '../config';
import { environment } from '../environments/environment';
import { EFlag } from '../modules/flags/models/EFlag';
import { ELogActionId } from '../modules/logger/models/ELogActionId';
import { PatchService } from '../modules/patch/services/patch.service';
import { AppComponentBase } from '../modules/utils/components/app-component-base';
import { SceEventService } from './features/sce4Mobile/sce-event.service';
import { SceFactory } from './features/sce4Mobile/sceFactory';
import { ConfirmationMajAppComponent } from './features/shared/components/confirmation-maj-app/confirmation-maj-app.component';
import { LoaderComponent } from './features/shared/components/loader/loader.component';
import { EDeviceType } from './features/shared/enums/EDeviceType';
import { AuthenticatorService } from './features/shared/services/authenticator.service';
import { DeviceService } from './features/shared/services/device.service';
import { DrawerPopoverService } from './features/shared/services/drawer-popover.service';
import { LoaderService } from './features/shared/services/loader.service';
import { PanneauService } from './features/shared/services/panneau.service';
import { PopoverService } from './features/shared/services/popover.service';
import { VersionCheckService } from './features/shared/services/version-check.service';

@Component({
	selector: "ion-anakin",
	templateUrl: 'anakin.component.html',
	styleUrls: ['./anakin.component.scss']
})
export class AnakinComponent extends AppComponentBase implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('panneauContainer', { read: ViewContainerRef }) panneauContainer: ViewContainerRef;
	@ViewChild('popoverContainer', { read: ViewContainerRef }) popoverContainer: ViewContainerRef;
	@ViewChild('drawerContainer', { read: ViewContainerRef }) drawerContainer: ViewContainerRef;
	@ViewChild('loaderContainer', { read: ViewContainerRef }) loaderContainer: ViewContainerRef;

	private moHomeActivePageManager: ActivePageManager;
	private subscription: Subscription;
	public readonly logSourceId = "IDL.APP.C::";

	public isMobile = false;
	public isMultiWS = false;

	public pluginSce : CapacitorPluginScePlugin;
	

	public isAuthenticated: boolean;
	public menuOpened = false;


	constructor(
		private svcConfig: ConfigService,
		private svcSecurity: SecurityService,
		private svcLogger: LoggerService,
		private svcFlag: FlagService,
		private svcPatch: PatchService,
		private svcBattery: BatteryService,
		private svcApplication: ApplicationService,
		private svcEntityLink: EntityLinkService,
		private readonly svcPlatform: PlatformService,
		private svcSceEvent : SceEventService,
		
		router: Router,
		/** Service de menu angular. */
		private menuCtlr: MenuController,
		private svcDevice: DeviceService,
		private svcAuthenticator: AuthenticatorService,
		private svcPanneau: PanneauService,
		private svcDrawerPopover: DrawerPopoverService,
		private svcPopover: PopoverService,
		private svcPwaUpdate: PwaUpdateService,
		private svcLoader: LoaderService,
		private cdr: ChangeDetectorRef,
		private svcDeeplink: DeeplinkService,
		private svcVersionCheck : VersionCheckService
	) {
		super(svcApplication, svcPlatform, svcBattery);
		this.svcDeeplink.init();
		// psvcPageManager.addComponents(IdlComponentRegister.getIdlRouteComponents());
		// psvcRapport.initNumberOfNonCompletedTransmission();
		// psvcTraitement.initNumberOfTraitEndingSoon();
		this.moHomeActivePageManager = new ActivePageManager(this, router, (newUrl: string, pageUrl: string) => newUrl === "/home");
		const deviceTypeUser: EDeviceType = this.svcPlatform.getDeviceType();
		this.pluginSce = SceFactory.getSceInstance(deviceTypeUser)
	}

	public ngOnInit(): void {
		this.svcLoader.showLoader("Chargement de l'application...");
		console.log("IDL.APP.C:: En attente d'initialisation de la plateforme...");

		constants.environment.version = packageJson.version;

		this.svcPatch.applyPatches() // Application des patchs.
			.pipe(
				tap(_ => this.svcLoader.hideLoader()),
				mergeMap(_ => this.svcConfig.init(constants as IConfig)),
				tap(
					_ => {
						this.svcLogger.action(this.logSourceId, "Démarrage de l'application.", ELogActionId.appStart, { version: ConfigData.appInfo.appVersion, environment: ConfigData.environment.id } as IAppStartPauseLogData);
						this.svcFlag.setFlagValue(EFlag.appInitialized, true);
						console.log("IDL.APP.C:: Application initialized");
						if(this.svcPlatform.isMobileApp)
						{
							this.svcVersionCheck.checkVersion(async (remoteVersion) => {
								console.warn("Mise a jour demandé : ",remoteVersion);
								this.svcDrawerPopover.open("Nouvelle version de l\'application", "30%", null, ConfirmationMajAppComponent, {
									onConfirm: () => this.handleConfirmMajApp(),
									onCancel: () => this.handleCancelMajApp()
								}, null, null)							
							});
						}
						
					},
					poError => this.initError(poError)
				),
				mergeMap(() => this.initRouteSubscriptions()),
			)
			.subscribe();

		this.svcPwaUpdate.initUpdateCheck();
		

		this.subscription = this.svcDevice.isMobile$.subscribe((flag: boolean) => {
			this.isMobile = flag;
		});

		this.subscription = this.svcDevice._isMultiWS$.subscribe((isMultiWSFlag: boolean) => {
			this.isMultiWS = isMultiWSFlag;
		});

		this.subscription = this.svcSecurity.anakinAuthenticationStatus$.subscribe((flag: IAuthStatus) => {
			if (this.isAuthenticated && !flag.isAuthenticated) {
				this.svcAuthenticator.setAuthenticationStatus(false);
			}
		});

		this.svcAuthenticator.getAuthenticationStatus().subscribe((authenticatorStatus: boolean) => {
			this.isAuthenticated = authenticatorStatus;
			if (!authenticatorStatus) {
				this.menuOpened = false;
			}
		});
		if(this.svcPlatform.isMobileApp && this.svcPlatform.isIOS)
			this.setupAppListeners();
	}

	private setupAppListeners(): void {
		App.addListener('appStateChange', ({ isActive }) => {

		if (isActive) {
			// L'utilisateur revient dans l'application.
			console.warn(`[COMP] : App State: Reprise (Foreground).`);
			this.onAppResume(); 
		} else {
			// L'utilisateur quitte l'application ou l'envoie en arrière-plan.
			console.warn(`[COMP] : App State: Pause (Background).`);
			this.onAppPause();
		}
    });

    // Événement déclenché lors de la terminaison forcée (kill) de l'application.
    App.addListener('appTerminate' as any, () => {
    	console.warn(`[COMP] : App State: Terminaison forcée (Kill).`);
			this.onAppPause(); 
		});
  	}

	private async onAppPause(): Promise<void> {
		if(ConfigData.sceInitialize && this.isAuthenticated){
			console.warn('Terminate complète du plugin SCE');
    		await this.pluginSce.terminate();
			ConfigData.sceInitialize = false;
			this.svcSceEvent.removeSceEventListener()
		}
	}

	private onAppResume(): void {
		this.svcSceEvent.initialize();
  	}

	private handleConfirmMajApp() {
  		this.svcDrawerPopover.close();

		let storeUrl = '';

		if (this.svcPlatform.isAndroid) {
			const widgetId = this.svcPlatform.widgetId;
			storeUrl = `https://play.google.com/store/apps/details?id=${widgetId}`;
		}

		if (this.svcPlatform.isIOS) {
			const appleAppId = environment.appleAppId;
			storeUrl = `https://apps.apple.com/app/id${appleAppId}`;
		}

		if (storeUrl) {
			window.open(storeUrl, '_system');
		} else {
			console.warn('Plateforme non supportée pour la redirection vers le store.');
		}
	}

	private handleCancelMajApp() {
		this.svcDrawerPopover.close();
		App.exitApp();
	}

	public ngOnDestroy(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	public ngAfterViewInit(): void {
		const componentRef = this.panneauContainer.createComponent(PanneauDesktopComponent);
		this.svcPanneau.setPanneauComponentRef(componentRef);
		componentRef.instance.open = false;

		const loaderRef = this.loaderContainer.createComponent(LoaderComponent);
		this.svcLoader.setLoaderComponentRef(loaderRef);
		loaderRef.instance.visible = false;

		const popoverRef = this.popoverContainer.createComponent(PopoverComponent);
		const drawerRef = this.drawerContainer.createComponent(SwipeableDrawerComponent);
		this.svcDrawerPopover.setPopoverComponentRef(popoverRef);
		this.svcDrawerPopover.setDrawerComponentRef(drawerRef);
		this.svcPopover.setPopoverComponentRef(popoverRef);
		popoverRef.instance.open = false;
		drawerRef.instance.visible = false;
		this.cdr.detectChanges();
	}

	private initRouteSubscriptions(): Observable<boolean> {
		return this.moHomeActivePageManager.isActive$.pipe(
			mergeMap((pbIsActive: boolean) => {
				if (pbIsActive && !ObjectHelper.isNullOrEmpty(this.svcEntityLink.currentEntity))
					return this.svcEntityLink.clearCurrentEntity(this.svcEntityLink.currentEntity.id);
				return EMPTY;
			})
		);
	}

	private initError(poError: string | DeviceNotAuthorizedError | any): void {
		console.error(`IDL.APP.C:: Application initialization error.`, poError);
	}

	onMenuToggle = () => {
		this.menuOpened = !this.menuOpened;
	}

}
